/**
 * Offline draft of an inspection, kept in IndexedDB on the device (never in localStorage) and pushed to the server
 * only when the inspector submits. One draft per user and equipment, so a shared phone never mixes drafts.
 */
import { deleteDB, openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { Level, OperatingState, Rules } from '../../api/types'

export interface DraftObservation {
  level?: Level | null
  rawValue?: string
  note?: string
  motorLevel?: string | null
}

export interface DraftPhoto {
  id: string
  name: string
  type: string
  data: ArrayBuffer
  /** Criterion the photo belongs to; empty = the whole inspection. */
  criterion?: string
  uploaded: boolean
}

/** Informational readings from the equipment sheet; strings as typed, never scored. */
export interface SheetReadings {
  vibrationLevel?: string
  vibrationMmS?: string
  currentA?: string
  voltageV?: string
  frequencyHz?: string
  reducerTemperatureC?: string
  motorTemperatureC?: string
  accessories?: string
}

export type DraftStatus = 'editing' | 'pending_sync' | 'synced'

export interface InspectionDraft {
  key: string
  userId: number
  equipmentId: number
  equipmentCode: string
  inspectedOn: string
  operatingState: OperatingState
  notes: string
  technicianName?: string
  diagnosis?: string
  recommendations?: string
  /** Absent in drafts saved before these readings existed. */
  sheet?: SheetReadings
  observations: Record<string, DraftObservation>
  /** Answers to the helper questions, by criterion. */
  helpers: Record<string, Record<string, string>>
  photos: DraftPhoto[]
  templateVersionId?: number
  /** Rules of that version, kept with the draft so the score preview and the form work offline. */
  rules?: Rules
  /** Set once the server has a draft for it, so a retry never creates a second inspection. */
  serverInspectionId?: number
  status: DraftStatus
  updatedAt: number
}

interface Schema extends DBSchema {
  drafts: { key: string; value: InspectionDraft; indexes: { 'by-user': number } }
}

const DB_NAME = 'motores-drafts'
let dbPromise: Promise<IDBPDatabase<Schema>> | null = null

function db(): Promise<IDBPDatabase<Schema>> {
  dbPromise ??= openDB<Schema>(DB_NAME, 1, {
    upgrade(database) {
      const store = database.createObjectStore('drafts', { keyPath: 'key' })
      store.createIndex('by-user', 'userId')
    },
  })
  return dbPromise
}

/** Test helper: drop everything (also used on logout so the next user starts clean). */
export async function resetDraftDb(): Promise<void> {
  if (dbPromise) (await dbPromise).close()
  dbPromise = null
  await deleteDB(DB_NAME)
}

export function draftKey(userId: number, equipmentCode: string): string {
  return `${userId}:${equipmentCode}`
}

export function blankDraft(init: { userId: number; equipmentId: number; equipmentCode: string; inspectedOn: string }): InspectionDraft {
  return {
    key: draftKey(init.userId, init.equipmentCode),
    userId: init.userId,
    equipmentId: init.equipmentId,
    equipmentCode: init.equipmentCode,
    inspectedOn: init.inspectedOn,
    operatingState: 'running',
    notes: '',
    observations: {},
    helpers: {},
    photos: [],
    status: 'editing',
    updatedAt: Date.now(),
  }
}

export const DRAFTS_CHANGED = 'motores:drafts-changed'
const announce = () => window.dispatchEvent(new Event(DRAFTS_CHANGED))

export async function saveDraft(draft: InspectionDraft): Promise<InspectionDraft> {
  const stamped = { ...draft, updatedAt: Date.now() }
  await (await db()).put('drafts', stamped)
  announce()
  return stamped
}

export async function loadDraft(userId: number, equipmentCode: string): Promise<InspectionDraft | null> {
  return (await (await db()).get('drafts', draftKey(userId, equipmentCode))) ?? null
}

export async function deleteDraft(userId: number, equipmentCode: string): Promise<void> {
  await (await db()).delete('drafts', draftKey(userId, equipmentCode))
  announce()
}

export async function listDrafts(userId: number): Promise<InspectionDraft[]> {
  const all = await (await db()).getAllFromIndex('drafts', 'by-user', userId)
  return all.sort((a, b) => b.updatedAt - a.updatedAt)
}

export function setObservation(draft: InspectionDraft, criterion: string, patch: DraftObservation): InspectionDraft {
  return { ...draft, observations: { ...draft.observations, [criterion]: { ...draft.observations[criterion], ...patch } } }
}

export function summarizeDraft(draft: InspectionDraft): { answered: number; photos: number } {
  return {
    answered: Object.values(draft.observations).filter((o) => o.level).length,
    photos: draft.photos.length,
  }
}
